import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { ArrowRight, CheckCircle2, Heart, ShieldCheck } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { trackCTA, trackConversion } from "@/lib/tracking";
import { COMPASSION_PLAN } from "@/lib/fsap-data";

/** Approved monthly rates for the Compassion Plan, in cents. */
export const COMPASSION_RATE_OPTIONS = [
  { cents: 9900, label: "$99 / month", note: "SSI-level income (≈ $888/mo)" },
  { cents: 11400, label: "$114 / month", note: "Limited income with some flexibility" },
  { cents: 12900, label: "$129 / month", note: "Reduced rate, partial hardship" },
] as const;

const SERVICE_OPTIONS = [
  "ARD/IEP meeting representation",
  "Monthly strategy calls",
  "Records / compliance review",
  "Agency or provider meetings",
  "Written meeting prep & recaps",
  "Crisis or escalation support",
];

const INCOME_SOURCES = [
  "SSI / SSDI",
  "Employment wages",
  "Family support",
  "Unemployment / disability leave",
  "Other / mixed",
];

const schema = z.object({
  full_name: z.string().trim().min(2, "Please enter your full name").max(120),
  email: z.string().trim().email("Valid email required").max(254),
  phone: z.string().trim().max(30).optional(),
  city_state: z.string().trim().max(120).optional(),
  household_size: z.coerce.number().int().min(1, "At least 1").max(30).optional(),
  monthly_income: z.coerce.number().min(0).max(1_000_000).optional(),
  income_source: z.string().max(80).optional(),
  requested_monthly_cents: z.number().refine(
    (v) => COMPASSION_RATE_OPTIONS.some((o) => o.cents === v),
    "Choose a monthly rate",
  ),
  hardship_description: z
    .string()
    .trim()
    .min(20, "Please share at least a sentence or two (20+ characters)")
    .max(5000),
  services_requested: z.array(z.string()).min(1, "Select at least one service"),
  documentation_notes: z.string().trim().max(1000).optional(),
  consent_income_review: z.literal(true, { message: "Required to review your request" }),
  consent_advocacy_only: z.literal(true, { message: "Required to proceed" }),
});

const inputCls =
  "w-full bg-bg-primary border border-fg-muted/25 px-4 py-3 text-sm focus:border-gold outline-none";
const labelCls = "block text-xs uppercase tracking-[0.14em] text-fg-muted mb-2";

const CompassionApply = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city_state: "",
    household_size: "",
    monthly_income: "",
    income_source: "",
    requested_monthly_cents: 9900 as number,
    hardship_description: "",
    services_requested: [] as string[],
    documentation_notes: "",
    consent_income_review: false,
    consent_advocacy_only: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (value: string) =>
    setForm((f) => ({
      ...f,
      services_requested: f.services_requested.includes(value)
        ? f.services_requested.filter((v) => v !== value)
        : [...f.services_requested, value],
    }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({
      ...form,
      email: form.email || user?.email || "",
      household_size: form.household_size === "" ? undefined : form.household_size,
      monthly_income: form.monthly_income === "" ? undefined : form.monthly_income,
    });
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const iss of parsed.error.issues) {
        const key = iss.path[0] as string;
        if (!fe[key]) fe[key] = iss.message;
      }
      setErrors(fe);
      trackCTA("compassion_apply_validation_error", { count: Object.keys(fe).length });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const d = parsed.data;
      const { error } = await supabase.from("compassion_enrollments").insert({
        user_id: user?.id ?? null,
        full_name: d.full_name,
        email: d.email,
        phone: d.phone || null,
        city_state: d.city_state || null,
        household_size: d.household_size ?? null,
        monthly_income_cents:
          d.monthly_income === undefined ? null : Math.round(d.monthly_income * 100),
        income_source: d.income_source || null,
        requested_monthly_cents: d.requested_monthly_cents,
        hardship_description: d.hardship_description,
        services_requested: d.services_requested,
        documentation_notes: d.documentation_notes || null,
        consent_income_review: true,
        consent_advocacy_only: true,
        stripe_price_id: "fsap_compassion_monthly",
        source: "compassion_apply_page",
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : null,
      });
      if (error) throw error;
      trackConversion("compassion_apply_submit", {
        requested_monthly_cents: d.requested_monthly_cents,
        services: d.services_requested.join(","),
      });
      setSuccess(true);
    } catch (err) {
      setErrors({ _root: err instanceof Error ? err.message : "Submission failed" });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-bg-primary text-fg-primary">
        <PageMeta
          title="Compassion Plan request received | Dr. Sully"
          description="Your Accessible FSAP Compassion Plan enrollment request has been received for review."
          path="/advocacy/compassion-apply"
          noindex
        />
        <SiteNav />
        <main className="max-w-2xl mx-auto px-6 py-32 text-center">
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-6" />
          <h1 className="font-display text-4xl mb-4">Request received — pending review.</h1>
          <p className="text-fg-secondary mb-4">
            Your Compassion Plan enrollment is <strong>not active yet</strong>. Every request is
            reviewed by hand so the reduced rate reaches the families it was built for. Expect a
            decision within 3–5 business days.
          </p>
          <p className="text-fg-secondary mb-8">
            Once approved, you'll receive a secure link to start your monthly plan at the approved
            rate. No payment is collected before approval.
          </p>
          <Link
            to="/advocacy"
            className="inline-flex items-center gap-2 border border-gold/50 px-6 py-3 font-display uppercase tracking-[0.18em] text-xs"
          >
            Back to Advocacy <ArrowRight className="h-4 w-4" />
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary text-fg-primary">
      <PageMeta
        title="Apply for the Accessible FSAP Compassion Plan | Dr. Sully"
        description="Request enrollment in the Accessible FSAP Compassion Plan ($99–$129/mo). Every request is reviewed before the monthly plan can start."
        path="/advocacy/compassion-apply"
      />
      <SiteNav />
      <main>
        <section className="border-b border-fg-muted/10 bg-bg-secondary">
          <div className="max-w-3xl mx-auto px-6 py-16">
            <p className="font-display uppercase tracking-[0.24em] text-gold text-xs mb-3 flex items-center gap-2">
              <Heart className="h-4 w-4" /> Accessible FSAP
            </p>
            <h1 className="font-display text-4xl md:text-5xl leading-tight">
              Apply for the Compassion Plan.
            </h1>
            <p className="text-fg-secondary mt-4">
              {COMPASSION_PLAN.purpose}
            </p>
            <div className="mt-6 flex items-start gap-3 border border-gold/30 bg-gold/5 p-4 text-sm">
              <ShieldCheck className="h-5 w-5 text-gold shrink-0 mt-0.5" />
              <p>
                Approval is required before billing begins. Nothing is charged today — after review,
                approved households receive a secure checkout link at their approved monthly rate
                ($99–$129). Enrollment is reviewed quarterly.
              </p>
            </div>
          </div>
        </section>

        <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-12 space-y-10">
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Who you are</legend>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="cp-name">Full name *</label>
                <input
                  id="cp-name"
                  className={inputCls}
                  value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                />
                {errors.full_name && <p className="text-red-400 text-xs mt-1">{errors.full_name}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="cp-email">Email *</label>
                <input
                  id="cp-email"
                  type="email"
                  className={inputCls}
                  value={form.email || user?.email || ""}
                  readOnly={!!user?.email}
                  onChange={(e) => set("email", e.target.value)}
                />
                {user?.email ? (
                  <p className="text-xs text-fg-muted mt-1">
                    Using your signed-in account email so approval links reach you.
                  </p>
                ) : null}
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className={labelCls} htmlFor="cp-phone">Phone</label>
                <input
                  id="cp-phone"
                  className={inputCls}
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="cp-city">City, State</label>
                <input
                  id="cp-city"
                  className={inputCls}
                  placeholder="San Antonio, TX"
                  value={form.city_state}
                  onChange={(e) => set("city_state", e.target.value)}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Financial picture</legend>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className={labelCls} htmlFor="cp-household">Household size</label>
                <input
                  id="cp-household"
                  type="number"
                  min={1}
                  max={30}
                  className={inputCls}
                  value={form.household_size}
                  onChange={(e) => set("household_size", e.target.value)}
                />
                {errors.household_size && (
                  <p className="text-red-400 text-xs mt-1">{errors.household_size}</p>
                )}
              </div>
              <div>
                <label className={labelCls} htmlFor="cp-income">Monthly household income ($)</label>
                <input
                  id="cp-income"
                  type="number"
                  min={0}
                  step="1"
                  className={inputCls}
                  value={form.monthly_income}
                  onChange={(e) => set("monthly_income", e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="cp-source">Primary income source</label>
                <select
                  id="cp-source"
                  className={inputCls}
                  value={form.income_source}
                  onChange={(e) => set("income_source", e.target.value)}
                >
                  <option value="">Select…</option>
                  {INCOME_SOURCES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <span className={labelCls}>Requested monthly rate *</span>
              <div className="grid md:grid-cols-3 gap-3">
                {COMPASSION_RATE_OPTIONS.map((o) => (
                  <button
                    key={o.cents}
                    type="button"
                    onClick={() => set("requested_monthly_cents", o.cents)}
                    aria-pressed={form.requested_monthly_cents === o.cents}
                    className={`border p-4 text-left transition ${
                      form.requested_monthly_cents === o.cents
                        ? "border-gold bg-gold/10"
                        : "border-fg-muted/25 hover:border-gold/50"
                    }`}
                  >
                    <span className="font-display text-lg block">{o.label}</span>
                    <span className="text-xs text-fg-muted">{o.note}</span>
                  </button>
                ))}
              </div>
              {errors.requested_monthly_cents && (
                <p className="text-red-400 text-xs mt-1">{errors.requested_monthly_cents}</p>
              )}
            </div>

            <div>
              <label className={labelCls} htmlFor="cp-hardship">
                Describe your hardship and what support you need *
              </label>
              <textarea
                id="cp-hardship"
                rows={5}
                className={inputCls}
                value={form.hardship_description}
                onChange={(e) => set("hardship_description", e.target.value)}
              />
              {errors.hardship_description && (
                <p className="text-red-400 text-xs mt-1">{errors.hardship_description}</p>
              )}
            </div>

            <div>
              <label className={labelCls} htmlFor="cp-docs">
                Documentation you can provide if requested
              </label>
              <textarea
                id="cp-docs"
                rows={3}
                className={inputCls}
                placeholder="SSI award letter, pay stubs, benefits statement, Medicaid letter…"
                value={form.documentation_notes}
                onChange={(e) => set("documentation_notes", e.target.value)}
              />
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="font-display text-xl mb-2">Support you're requesting *</legend>
            <div className="grid md:grid-cols-2 gap-2">
              {SERVICE_OPTIONS.map((s) => (
                <label key={s} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.services_requested.includes(s)}
                    onChange={() => toggleService(s)}
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
            {errors.services_requested && (
              <p className="text-red-400 text-xs mt-1">{errors.services_requested}</p>
            )}
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="font-display text-xl mb-2">Consents</legend>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.consent_income_review}
                onChange={(e) => set("consent_income_review", e.target.checked)}
              />
              <span>
                I understand enrollment is reviewed before it becomes active, documentation of
                income may be requested, and enrollment is re-reviewed quarterly.
              </span>
            </label>
            {errors.consent_income_review && (
              <p className="text-red-400 text-xs">{errors.consent_income_review}</p>
            )}
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.consent_advocacy_only}
                onChange={(e) => set("consent_advocacy_only", e.target.checked)}
              />
              <span>
                I understand FSAP provides advocacy and support — not legal advice, medical advice,
                or therapy — and that no outcome is guaranteed.
              </span>
            </label>
            {errors.consent_advocacy_only && (
              <p className="text-red-400 text-xs">{errors.consent_advocacy_only}</p>
            )}
          </fieldset>

          {errors._root && (
            <p role="alert" className="text-red-400 text-sm">{errors._root}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-4 hover:brightness-110 disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit for review"}
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="text-xs text-fg-muted">
            No card is collected on this form. Billing starts only after an approval decision.
          </p>
        </form>
      </main>
      <SiteFooter />
    </div>
  );
};

export default CompassionApply;
